import NameBadge from '@/components/Badge/NameBadge';
// import { ReactComponent as PaymentPersonalizationIcon } from '@icons/payment_personalization.svg';
// import { ReactComponent as ReturnsOrchestratorIcon } from '@icons/returns_ochestrator.svg';
// import { ReactComponent as IdentityIcon } from '@icons/tamperproof.svg';
import { ScoreBubble } from '@/pages/dashboard/components/shoppersCharts';
import { Config } from '@/redux/slice/projectSlice';
import moment from 'moment';
import {
  config_type,
  CONSUMER_TRUST_INDEX,
  PAYEE_TRUST_INDEX,
  // IDENTITY_AND_AUTH,
  // PAYMENT_PERSONALIZATION,
  RETURNS_ORCHESTRATOR,
  ROLES,
  ShopperConfigSequence,
} from './constants';
import { TrudentyData } from '@/interface/ReasonCode.interface';
import { NetworkData } from '@/pages/dashboard/TrustNetworkGraph';

export const getRandomInt = (max?: number) => {
  return Math.floor(Math.random() * (max ?? 10000000));
};

export const generateLast12Months = (
  labelFormat: string = `MMM 'YY`,
  valueFormat: string = 'YYYY-MM'
) => {
  const months = [];
  let currentMonth = moment();

  for (let i = 0; i < 12; i++) {
    months.push({
      label: currentMonth.format(labelFormat),
      value: currentMonth.format(valueFormat),
    });
    currentMonth = currentMonth.subtract(1, 'month');
  }
  return months;
};

export const generateLastMonths = (number: number = 8) => {
  const months = [];
  let currentMonth = moment();

  for (let i = 0; i < number; i++) {
    months.push(currentMonth.format(`MMM`));
    currentMonth = currentMonth.subtract(1, 'month');
  }
  return months;
};

export const getClassNames = (...classes: (string | undefined)[]) => {
  return classes.filter(Boolean).join(' ');
};

export const toCamelCase = (str: string) => {
  return str
    .replace(/\s(.)/g, function (a) {
      return a.toUpperCase();
    })
    .replace(/\s/g, '')
    .replace(/^(.)/, function (b) {
      return b.toLowerCase();
    });
};

export const URLPattern2 =
  /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm;
export const emailPattern = /^\w+([.-]\w+)*@\w+([.-]\w+)*(\.\w+)+$/;

export const URLPattern =
  /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)/g;

export const getRiskColor = (
  risk: string,
  inactive: boolean | undefined = false,
  isPayeeProduct: boolean | undefined = false,
  isConsentActive: boolean | undefined = true
) => {
  if (inactive) return 'bg-grey-content-tertiary';
  if (!isConsentActive) return 'bg-grey-content-secondary';
  switch (risk) {
    case 'trusted':
      return isPayeeProduct ? 'bg-tblue' : 'bg-risk-green';
    case 'medium':
      return 'bg-trud-orange';
    case 'high':
      return 'bg-risk-red';
    case 'low':
      return 'bg-tblue';
    default:
      return 'bg-grey-content-tertiary';
  }
};

export const getRiskTitle = (risk: string, isPayeeProduct?: boolean) => {
  switch (risk) {
    case 'trusted':
    case 'LOW_RISK':
      return isPayeeProduct ? 'High Trust Payee' : 'High Trust Consumer';
    case 'medium':
    case 'MEDIUM_RISK':
      return isPayeeProduct ? 'Medium Trust Payee' : 'Medium Trust Consumer';
    case 'high':
    case 'HIGH_RISK':
      return isPayeeProduct ? 'Low Trust Payee' : 'Low Trust Consumer';
    default:
      return '';
  }
};
export const getRiskWithScore = (score: number) => {
  if (score >= 80) {
    return 'trusted';
  } else if (score >= 70) {
    return 'medium';
  } else {
    return 'high';
  }
};

export const riskLevelsData = {
  'High Trust': [
    'LOW_RISK_CONSUMER',
    'LOW_RISK',
    'TRUSTED',
    'HIGH_TRUST',
    'High Trust',
    'High trust',
  ],
  'Medium Trust': [
    'MEDIUM_RISK_CONSUMER',
    'MEDIUM_TRUSTED',
    'MEDIUM_RISK',
    'MEDIUM_HIGH_RISK',
    'MEDIUM_TRUST',
    'Medium Trust',
    'Medium trust',
  ],
  'Low Trust': ['HIGH_RISK', 'HIGH_RISK_CONSUMER', 'LOW_TRUST', 'Low Trust', 'Low trust'],
};

export const getRandomLightColor = (type: 'light' | 'dark' = 'light'): string => {
  const hue = Math.floor(Math.random() * 360); // any color
  const saturation = 60 + Math.random() * 20; // 60–80%
  const lightness = type === 'light' ? 70 + Math.random() * 10 : 50 + Math.random() * 10; // 85–95% (light)

  const hslToHex = (h: number, s: number, l: number): string => {
    s /= 100;
    l /= 100;

    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) =>
      Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));

    return `#${[f(0), f(8), f(4)].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
  };

  return hslToHex(hue, saturation, lightness);
};

export const getColorCode = (risk_level: string, text: string = 'text') => {
  let color = '';
  if (risk_level === 'body-black') color = 'body-black';
  else if (riskLevelsData['Low Trust'].includes(risk_level)) color = 'risk-red';
  else if (riskLevelsData['Medium Trust'].includes(risk_level) || risk_level === '')
    color = 'trud-orange';
  else color = 'score-green';
  return text === 'text' ? `text-${color}` : `bg-${color}`;
};

export const getScoreColor = (score: number, inactive = false) => {
  if (inactive) {
    return 'text-grey-content-tertiary';
  } else if (score >= 80) {
    return 'text-score-green';
  } else if (score >= 70) {
    return 'text-trud-orange';
  } else {
    return 'text-red-alert';
  }
};

export const ReasonCodeRiskLevel = (value: {
  classname?: string;
  score: number;
  risk_level_criteria: {
    high_risk_threshold: number;
    medium_risk_threshold: number;
    max_score: number;
  };
}) => {
  let risk = 'LOW_RISK';
  const { classname = '', risk_level_criteria, score } = value;
  if (Math.round(score) > Math.round(risk_level_criteria?.medium_risk_threshold || 0)) {
    risk = 'LOW_RISK';
  } else if (
    Math.round(score) > Math.round(risk_level_criteria?.high_risk_threshold || 0)
  ) {
    risk = 'MEDIUM_RISK';
  } else {
    risk = 'HIGH_RISK';
  }
  return (
    <div
      className={getClassNames(
        'rounded py-1 px-3 border border-grey-stroke-primary text-sm text-grey-content-secondary bg-white w-fit h-[21px]',
        'flex items-center gap-1 justify-center',
        classname
      )}
    >
      <div
        className={getClassNames('w-2 h-2 rounded-full', getColorCode(risk, 'bg'))}
      ></div>
      {risk === 'LOW_RISK'
        ? 'High trust'
        : risk === 'MEDIUM_RISK'
          ? 'Medium trust'
          : 'Low trust'}
    </div>
  );
};

export const ReasonCode = (
  code: string,
  data: string | null,
  risk_level: string = 'LOW_RISK_CONSUMER',
  className: string = '',
  boxClassName: string = ''
) => {
  return (
    <span
      className={`grid grid-cols-[70px,1fr] py-2 px-4 bg-body-variant2 w-full flex items-center gap-2 ${boxClassName}`}
    >
      <div className="flex justify-center">
        <span
          className={getClassNames(
            'rounded px-2 border border-grey-content-quaternary text-sm1 bg-white w-fit h-[23px]',
            'flex items-center justify-center',
            getColorCode(risk_level)
          )}
        >
          {code}
        </span>
      </div>
      <span className={className}>{data ? data : ''}</span>
    </span>
  );
};

export const TrustDimenstionReasonCode = (
  code: string,
  data: string | null,
  score: number,
  risk_level_criteria: {
    high_risk_threshold: number;
    medium_risk_threshold: number;
    max_score: number;
  }
) => {
  let risk = 'LOW_RISK';
  if (Math.round(score) > Math.round(risk_level_criteria?.medium_risk_threshold || 0)) {
    risk = 'LOW_RISK';
  } else if (
    Math.round(score) > Math.round(risk_level_criteria?.high_risk_threshold || 0)
  ) {
    risk = 'MEDIUM_RISK';
  } else {
    risk = 'HIGH_RISK';
  }
  return (
    <span className="grid grid-cols-[70px,1fr] py-2 px-4 bg-body-variant2 w-full flex items-center gap-2">
      <div className="flex justify-center">
        <span
          className={getClassNames(
            'rounded px-2 border border-grey-content-quaternary text-sm1 bg-white w-fit h-[23px]',
            'flex items-center justify-center',
            getColorCode(risk)
          )}
        >
          {code}
        </span>
      </div>
      <span>{data ? data : ''}</span>
    </span>
  );
};

export const Badge = ({
  code,
  risk_level = 'Low Trust',
  className = '',
  badgeClassName = '',
  tooltip,
  customTooltip,
}: {
  code: string;
  risk_level: string;
  className?: string;
  badgeClassName?: string;
  tooltip?: string;
  customTooltip?: React.ReactNode;
}) => {
  return (
    <div key={code} className={getClassNames('relative group', badgeClassName)}>
      <span
        className={getClassNames(
          'rounded px-2 border border-grey-stroke-primary text-sm1 bg-white w-fit h-[23px]',
          'flex items-center justify-center',
          className,
          getColorCode(risk_level)
        )}
      >
        {code}
      </span>
      {customTooltip
        ? customTooltip
        : tooltip && (
            <div className="max-w-[300px] w-max absolute left-0 top-7 z-10 bg-white2 border border-grey-stroke-primary shadow-md rounded p-2 text-sm1 text-body-black opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none transform">
              {tooltip}
            </div>
          )}
    </div>
  );
};

export const getRiskLevel = (risk_level: string) => {
  if (risk_level === 'LOW_RISK_CONSUMER') {
    return 'trusted';
  } else if (risk_level === 'MEDIUM_RISK_CONSUMER') {
    return 'medium';
  } else {
    return 'high';
  }
};

export const getRiskLevelColor = (
  risk_level: string,
  inactive = false,
  isPayeeProduct = false,
  isConsentActive = true
) => {
  if (inactive) {
    return 'text-grey-content-tertiary';
  } else if (!isConsentActive) {
    return 'text-grey-content-secondary';
  } else if (riskLevelsData['High Trust'].includes(risk_level)) {
    return isPayeeProduct ? 'text-tblue' : 'text-score-green';
  } else if (riskLevelsData['Medium Trust'].includes(risk_level)) {
    return 'text-trud-orange';
  } else {
    return 'text-red-alert';
  }
};

export const scorePercentage = (score: number) => {
  return (
    <span
      className={`text-${score >= 0 ? 'score-green' : 'red-alert'} font-medium text-sm leading-sm`}
    >
      {score > 0 ? '+' : ''}
      {score}%
    </span>
  );
};

export const pecks = {
  STI: ['Consumer Trust Index Configuration', 'Consumer Trust Network Search'],
  CRM: [
    'Consumer Trust Index Configuration',
    'Instant Refunds Orchestrator',
    'Trusted Consumer Network Search and Analytics',
  ],
};

export const getLocalStorageData = (storageName: string) => {
  const data = localStorage.getItem(storageName);
  return data ? JSON.parse(data) : null;
};

export const setLocalStorageData = (storageName: string, data: string) => {
  localStorage.setItem(storageName, data);
};

interface ProjectType {
  name: string;
  type: string;
  description: string;
  pecks: string[];
  icon: React.ReactComponentElement<any, any>;
}
export const projectTypes: ProjectType[] = [
  {
    name: 'Consumer Trust Index',
    type: CONSUMER_TRUST_INDEX,
    description:
      'Consumer Trust Index shares 360° consumer fraud risk intelligence across the 9 dimensions of first-party trust.',
    pecks: [
      'Consumer identifier',
      'Network search and analytics',
      'Data sharing',
      'Consumer Trust Index with Network Intelligence',
    ],
    icon: (
      <ScoreBubble
        {...{ score: 100, risk_level: 'LOW_RISK_CONSUMER', isPayeeProduct: false }}
      />
    ),
  },
  {
    name: 'Payee Trust Index',
    type: PAYEE_TRUST_INDEX,
    description:
      'A fraud risk assessment indicating whether the payee account in a transfer is linked to previous APP fraud and provides a confirmation of payee details.',
    pecks: [
      'Payee identifier',
      'Network search and analytics',
      'Network intelligence on known APP fraudsters',
      'Confirmation of Payee',
    ],
    icon: (
      <ScoreBubble
        {...{ score: 100, risk_level: 'LOW_RISK_CONSUMER', isPayeeProduct: true }}
      />
    ),
  },
  // {
  //   name: 'Refunds Orchestrator',
  //   type: RETURNS_ORCHESTRATOR,
  //   description:
  //     'An orchestrator helping operations team make automated and assisted decisions for refunds based on consumer fraud risk.',
  //   pecks: [
  //     'Consumer Trust Index',
  //     'Returns Orchestrator with alerts',
  //     'Network Search and Analytics',
  //   ],
  //   icon: <ReturnsOrchestratorIcon className="" />,
  // },
  // {
  //   name: 'Identity and authentication',
  //   type: IDENTITY_AND_AUTH,
  //   description:
  //     'Secure consumer accounts from login to returns, with streamlined passkeys linked directly to your web domain.',
  //   pecks: ['Consumer account creation ', 'Consumer authentication'],
  //   icon: <IdentityIcon className="" />,
  // },
  // {
  //   name: 'Payment Personalisation',
  //   type: PAYMENT_PERSONALIZATION,
  //   description:
  //     'Automatically personalise routing for payments based on consumer fraud risk profile, at checkout and refunds',
  //   pecks: ['Consumer Trust Index', 'Returns Orchestrator'],
  //   icon: <PaymentPersonalizationIcon className="" />,
  // },
];

export const modeColors: Record<string, string> = {
  LIVE: 'bg-green-alert',
  SANDBOX: 'bg-trud-orange',
};

export const addDays = (days: number) => {
  const result = new Date();
  result.setDate(result.getDate() + days);
  return result.toDateString();
};

export const members = [
  {
    id: 0,
    firstName: 'Lerato',
    lastName: 'Matsio',
    email: 'lerato@trudenty.com',
    lastSeen: '10 secs ago',
    role: 'admin',
    active: false,
  },
  {
    id: 1,
    firstName: 'Marco',
    lastName: 'Kome',
    email: 'marco@trudenty.com',
    lastSeen: 'Yesterday at 16:30',
    role: 'viewer',
    active: true,
  },
  {
    id: 2,
    firstName: 'Tuttuh',
    lastName: 'Adegun',
    email: 'tuttuh@trudenty.com',
    lastSeen: '10 secs ago',
    role: 'admin',
    active: false,
  },
  {
    id: 3,
    firstName: 'Ose Yaw',
    lastName: 'Ababio',
    email: 'ababio@trudenty.com',
    lastSeen: 'Yesterday at 16:30',
    role: 'viewer',
    active: true,
  },
  {
    id: 4,
    firstName: 'Amy',
    lastName: 'Fowler',
    email: 'amy@trudenty.com',
    lastSeen: '10 secs ago',
    role: 'admin',
    active: false,
  },
  {
    id: 5,
    firstName: 'Brian',
    lastName: 'Goldberg',
    email: 'brian@trudenty.com',
    lastSeen: '3 days ago',
    role: 'viewer',
    active: true,
  },
];

export const projectsOld = [
  {
    id: 1,
    name: 'Consumer Trust Index',
    description:
      'Safeguarding the entire online marketplace, ensuring a secure and seamless shopping environment for all.',
    products: CONSUMER_TRUST_INDEX,
    environment: 'LIVE',
    lastPublished: '',
  },
  {
    id: 2,
    name: 'Returns Orchestrator',
    description:
      'An orchestrator helping operations team make automated and assisted decisions for refunds based on consumer fraud risk.',
    products: RETURNS_ORCHESTRATOR,
    environment: 'SANDBOX',
    lastPublished: '',
  },
  {
    id: 3,
    name: 'Consumer Trust Index',
    description:
      'Safeguarding the entire online marketplace, ensuring a secure and seamless shopping environment for all.',
    products: CONSUMER_TRUST_INDEX,
    environment: 'SANDBOX',
    lastPublished: '',
  },
  {
    id: 4,
    name: 'Returns Orchestrator',
    description:
      'An orchestrator helping operations team make automated and assisted decisions for refunds based on consumer fraud risk.',
    products: RETURNS_ORCHESTRATOR,
    environment: 'LIVE',
    lastPublished: '',
  },
];

export const projects = [
  {
    api_key: 'sandbox_6b9e73d4-f845-41c9-ad8a-ee8757ec7c11',
    id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
    name: 'Consumer Trust Index',
    description:
      'Safeguarding the entire online marketplace, ensuring a secure and seamless shopping environment for all.',
    products: CONSUMER_TRUST_INDEX,
    environment: 'LIVE',
    status: 'draft',
    project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
    company_id: '60c3e8b0-8a2c-11ee-9f7e-e31dc2387503',
    createdAt: '2023-11-27T20:49:37.000Z',
    updatedAt: '2023-11-27T20:49:37.000Z',
    deletedAt: null,
    shopper_trust_index_configs: [
      {
        id: 'aca9e83a-41bc-47dd-8380-acaa5a3bd3a6',
        name: 'Identity',
        config_type: 'BANK_VERIFIED_IDENTITY',
        score: '10.00',
        recommended: '0.00',
        description:
          'Identity verification based on bank-verified identity & digital identity.',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: 'a5fbaacb-364b-4fcd-99da-a931a2e6747d',
        name: 'Network history with consumer',
        config_type: 'NETWORK_HISTORY_WITH_CONSUMER',
        score: '30.00',
        recommended: '0.00',
        description:
          "History of consumer's orders, returns and refunds in across the network including other ecommerce players.",
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: '758d101d-5dc2-4202-a2f2-b53e06925aaf',
        name: 'Adverse media scan',
        config_type: 'ADVERSE_MEDIA_SCAN',
        score: '4.00',
        recommended: '0.00',
        description: 'Web crawler for consumer activity on the dark web.',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },

      {
        id: '3d61acd7-ce6c-48e8-a055-97fb9364f62a',
        name: 'Consumer banking history',
        config_type: 'CONSUMER_BANKING_HISTORY',
        score: '35.00',
        recommended: '0.00',
        description:
          'Consumer bank activity levels including shopping returns and refunds history.',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },

      {
        id: '1f3326a5-a4d6-4b22-8cc7-ddf9ce80e481',
        name: "Retailer's own shopping history with consumer",
        config_type: 'RETAILERS_OWN_SHOPPING_HISTORY_WITH_CONSUMER',
        score: '20.00',
        recommended: '0.00',
        description: 'Description...',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
    ],
    trusted_shopper_score_configs: [
      {
        id: 'ce39fa69-e83d-4782-9305-2d3d875f489e',
        config_type: 'MEDIUM_RISK',
        name: 'A Medium Risk Consumer has an index score of...',
        score: '79.00',
        description:
          'Medium Risk Customerdshave a high assurance on their identity and a considerable ratio of returns and refunds, but no detected fraud.',
        recommended: '79.00',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: '1ae1bb6f-ec96-48e6-ae94-e5651ca2d948',
        config_type: 'LOW_RISK',
        name: 'A Trusted Consumer has an index score of ...',
        score: '69.00',
        description:
          'Trusted Customerdshave a high assurance on their identity and a low ratio of returns and refunds across the network.',
        recommended: '69.00',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: '13813ce4-28aa-4a7c-af76-d487615bcbf4',
        config_type: 'HIGH_RISK',
        name: 'A High Risk Consumer has an index score of...',
        score: '80.00',
        description:
          'High Risk Customerdshave medium to low levels on identity assurance, high ratio of returns and refunds and have return and refund issues including fraud.',
        recommended: '80.00',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
    ],
  },
  {
    api_key: 'live_8e6b75ab-cbf3-4ac0-81e5-e07a7bacdc80',
    id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
    name: 'Returns Orchestrator',
    description:
      'An orchestrator helping operations team make automated and assisted decisions for refunds based on consumer fraud risk.',
    products: RETURNS_ORCHESTRATOR,
    environment: 'SANDBOX',
    status: 'draft',
    project_id: null,
    company_id: '60c3e8b0-8a2c-11ee-9f7e-e31dc2387503',
    createdAt: '2023-11-27T20:49:37.000Z',
    updatedAt: '2023-11-27T20:49:37.000Z',
    deletedAt: null,
    shopper_trust_index_configs: [
      {
        id: 'e5628620-103d-467f-a006-4d35b40519cd',
        name: 'Adverse media scan',
        config_type: 'ADVERSE_MEDIA_SCAN',
        score: '4.00',
        recommended: '0.00',
        description: 'Web crawler for consumer activity on the dark web.',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: 'a0d07d1f-0e1a-4f01-9cbe-c429afbb1c1f',
        name: 'Network history with consumer',
        config_type: 'NETWORK_HISTORY_WITH_CONSUMER',
        score: '30.00',
        recommended: '0.00',
        description:
          "History of consumer's orders, returns and refunds in across the network including other ecommerce players.",
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: '9f21bc55-861a-400f-b871-15ecbcba3ee9',
        name: 'Consumer banking history',
        config_type: 'CONSUMER_BANKING_HISTORY',
        score: '35.00',
        recommended: '0.00',
        description:
          'Consumer bank activity levels including shopping returns and refunds history.',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },

      {
        id: '4f15255d-2935-49f8-9270-f9a65019b7ad',
        name: 'Identity',
        config_type: 'BANK_VERIFIED_IDENTITY',
        score: '10.00',
        recommended: '0.00',
        description:
          'Identity verification based on bank-verified identity & digital identity.',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },

      {
        id: '36b7136d-fe51-4118-87b9-31e6d710e63a',
        name: "Retailer's own shopping history with consumer",
        config_type: 'RETAILERS_OWN_SHOPPING_HISTORY_WITH_CONSUMER',
        score: '20.00',
        recommended: '0.00',
        description: 'Description...',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
    ],
    trusted_shopper_score_configs: [
      {
        id: 'ce94cf7a-13eb-453d-9318-849cfb4c52b2',
        config_type: 'HIGH_RISK',
        name: 'A High Risk Consumer has an index score of...',
        score: '80.00',
        description:
          'High Risk Customerdshave medium to low levels on identity assurance, high ratio of returns and refunds and have return and refund issues including fraud.',
        recommended: '80.00',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: 'a77e8e76-0344-4e80-94e0-ea7481552d42',
        config_type: 'LOW_RISK',
        name: 'A Trusted Consumer has an index score of ...',
        score: '69.00',
        description:
          'Trusted Customerdshave a high assurance on their identity and a low ratio of returns and refunds across the network.',
        recommended: '69.00',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: '6f2b4927-a381-4700-b39a-8823ec57062e',
        config_type: 'MEDIUM_RISK',
        name: 'A Medium Risk Consumer has an index score of...',
        score: '79.00',
        description:
          'Medium Risk Customerdshave a high assurance on their identity and a considerable ratio of returns and refunds, but no detected fraud.',
        recommended: '79.00',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
    ],
  },
  {
    api_key: 'sandbox_6b9e73d4-f845-41c9-ad8a-ee8757ec7c11',
    id: '10942e91-2c74-44c0-b78a-ed75c75f291E',
    name: 'Consumer Trust Index',
    description:
      'Safeguarding the entire online marketplace, ensuring a secure and seamless shopping environment for all.',
    products: CONSUMER_TRUST_INDEX,
    environment: 'SANDBOX',
    status: 'draft',
    project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
    company_id: '60c3e8b0-8a2c-11ee-9f7e-e31dc2387503',
    createdAt: '2023-11-27T20:49:37.000Z',
    updatedAt: '2023-11-27T20:49:37.000Z',
    deletedAt: null,
    shopper_trust_index_configs: [
      {
        id: 'aca9e83a-41bc-47dd-8380-acaa5a3bd3a6',
        name: 'Identity',
        config_type: 'BANK_VERIFIED_IDENTITY',
        score: '10.00',
        recommended: '0.00',
        description:
          'Identity verification based on bank-verified identity & digital identity.',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: 'a5fbaacb-364b-4fcd-99da-a931a2e6747d',
        name: 'Network history with consumer',
        config_type: 'NETWORK_HISTORY_WITH_CONSUMER',
        score: '30.00',
        recommended: '0.00',
        description:
          "History of consumer's orders, returns and refunds in across the network including other ecommerce players.",
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: '758d101d-5dc2-4202-a2f2-b53e06925aaf',
        name: 'Adverse media scan',
        config_type: 'ADVERSE_MEDIA_SCAN',
        score: '4.00',
        recommended: '0.00',
        description: 'Web crawler for consumer activity on the dark web.',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },

      {
        id: '3d61acd7-ce6c-48e8-a055-97fb9364f62a',
        name: 'Consumer banking history',
        config_type: 'CONSUMER_BANKING_HISTORY',
        score: '35.00',
        recommended: '0.00',
        description:
          'Consumer bank activity levels including shopping returns and refunds history.',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },

      {
        id: '1f3326a5-a4d6-4b22-8cc7-ddf9ce80e481',
        name: "Retailer's own shopping history with consumer",
        config_type: 'RETAILERS_OWN_SHOPPING_HISTORY_WITH_CONSUMER',
        score: '20.00',
        recommended: '0.00',
        description: 'Description...',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
    ],
    trusted_shopper_score_configs: [
      {
        id: 'ce39fa69-e83d-4782-9305-2d3d875f489e',
        config_type: 'MEDIUM_RISK',
        name: 'A Medium Risk Consumer has an index score of...',
        score: '79.00',
        description:
          'Medium Risk Customerdshave a high assurance on their identity and a considerable ratio of returns and refunds, but no detected fraud.',
        recommended: '79.00',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: '1ae1bb6f-ec96-48e6-ae94-e5651ca2d948',
        config_type: 'LOW_RISK',
        name: 'A Trusted Consumer has an index score of ...',
        score: '69.00',
        description:
          'Trusted Customerdshave a high assurance on their identity and a low ratio of returns and refunds across the network.',
        recommended: '69.00',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: '13813ce4-28aa-4a7c-af76-d487615bcbf4',
        config_type: 'HIGH_RISK',
        name: 'A High Risk Consumer has an index score of...',
        score: '80.00',
        description:
          'High Risk Customerdshave medium to low levels on identity assurance, high ratio of returns and refunds and have return and refund issues including fraud.',
        recommended: '80.00',
        project_id: '10942e91-2c74-44c0-b78a-ed75c75f291d',
        environment: 'LIVE',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
    ],
  },
  {
    api_key: 'live_8e6b75ab-cbf3-4ac0-81e5-e07a7bacdc80',
    id: '7b409b4d-80c7-4977-b4a2-1f28cd4a815E',
    name: 'Returns Orchestrator',
    description:
      'An orchestrator helping operations team make automated and assisted decisions for refunds based on consumer fraud risk.',
    products: RETURNS_ORCHESTRATOR,
    environment: 'LIVE',
    status: 'draft',
    project_id: null,
    company_id: '60c3e8b0-8a2c-11ee-9f7e-e31dc2387503',
    createdAt: '2023-11-27T20:49:37.000Z',
    updatedAt: '2023-11-27T20:49:37.000Z',
    deletedAt: null,
    shopper_trust_index_configs: [
      {
        id: 'e5628620-103d-467f-a006-4d35b40519cd',
        name: 'Adverse media scan',
        config_type: 'ADVERSE_MEDIA_SCAN',
        score: '4.00',
        recommended: '0.00',
        description: 'Web crawler for consumer activity on the dark web.',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: 'a0d07d1f-0e1a-4f01-9cbe-c429afbb1c1f',
        name: 'Network history with consumer',
        config_type: 'NETWORK_HISTORY_WITH_CONSUMER',
        score: '30.00',
        recommended: '0.00',
        description:
          "History of consumer's orders, returns and refunds in across the network including other ecommerce players.",
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: '9f21bc55-861a-400f-b871-15ecbcba3ee9',
        name: 'Consumer banking history',
        config_type: 'CONSUMER_BANKING_HISTORY',
        score: '35.00',
        recommended: '0.00',
        description:
          'Consumer bank activity levels including shopping returns and refunds history.',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },

      {
        id: '4f15255d-2935-49f8-9270-f9a65019b7ad',
        name: 'Identity',
        config_type: 'BANK_VERIFIED_IDENTITY',
        score: '10.00',
        recommended: '0.00',
        description:
          'Identity verification based on bank-verified identity & digital identity.',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },

      {
        id: '36b7136d-fe51-4118-87b9-31e6d710e63a',
        name: "Retailer's own shopping history with consumer",
        config_type: 'RETAILERS_OWN_SHOPPING_HISTORY_WITH_CONSUMER',
        score: '20.00',
        recommended: '0.00',
        description: 'Description...',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
    ],
    trusted_shopper_score_configs: [
      {
        id: 'ce94cf7a-13eb-453d-9318-849cfb4c52b2',
        config_type: 'HIGH_RISK',
        name: 'A High Risk Consumer has an index score of...',
        score: '80.00',
        description:
          'High Risk Customerdshave medium to low levels on identity assurance, high ratio of returns and refunds and have return and refund issues including fraud.',
        recommended: '80.00',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: 'a77e8e76-0344-4e80-94e0-ea7481552d42',
        config_type: 'LOW_RISK',
        name: 'A Trusted Consumer has an index score of ...',
        score: '69.00',
        description:
          'Trusted Customerdshave a high assurance on their identity and a low ratio of returns and refunds across the network.',
        recommended: '69.00',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
      {
        id: '6f2b4927-a381-4700-b39a-8823ec57062e',
        config_type: 'MEDIUM_RISK',
        name: 'A Medium Risk Consumer has an index score of...',
        score: '79.00',
        description:
          'Medium Risk Customerdshave a high assurance on their identity and a considerable ratio of returns and refunds, but no detected fraud.',
        recommended: '79.00',
        project_id: '7b409b4d-80c7-4977-b4a2-1f28cd4a8158',
        environment: 'SANDBOX',
        createdAt: '2023-11-27T20:49:38.000Z',
        updatedAt: '2023-11-27T20:49:38.000Z',
        deletedAt: null,
      },
    ],
  },
];

export function getMonthRange(startDate: Date, endDate: Date) {
  const resp = [];
  const activeDate = moment(startDate);
  while (activeDate.isSameOrBefore(moment(endDate))) {
    resp.push(String(activeDate.format('MMM DD')));
    activeDate.add(1, 'day');
  }
  return resp;
}

// Network Risk Distribution Series Value
export const seriesValue = (value: any[]) => {
  let result = [];
  if (value.length) {
    result = value.map((item: any) => item.value).reverse();
  }
  return result;
};

export function generateFirstLetter(name: string | undefined) {
  return name ? name.trim().slice(0, 1).toUpperCase() : '';
}

export const isSubmittable = (
  currentValues: { [key: string]: any },
  initialValues: { [key: string]: any },
  validateKeys: string[]
) => {
  let isValidDataFilled = true;
  validateKeys.forEach((data) => {
    if (currentValues[data] === initialValues[data]) {
      isValidDataFilled = false;
    }
  });
  return isValidDataFilled;
};

export function renderNameIcon({
  name,
  isCircle,
  className,
  textClassName,
  imageUrl,
  isLimitLongText = false,
}: {
  name: string;
  isCircle?: boolean;
  className?: string;
  textClassName?: string;
  imageUrl?: string;
  isLimitLongText?: boolean;
}) {
  return (
    <div className="flex justify-start">
      {imageUrl ? (
        <img
          alt=""
          src={imageUrl}
          className={`w-[22px] h-[22px] rounded ${className ?? ''} ${isCircle ? ' rounded-full' : ''} bg-white object-contain border border-stroke-primary !bg-grey-content-quaternary`}
        />
      ) : (
        <NameBadge
          FirstName={name}
          parentClass={`${isCircle ? ' rounded-full' : ''} ${className ?? ''}`}
        />
      )}

      <h6 className={` ${textClassName ?? ''}`}>
        {isLimitLongText && name?.length > 20 ? `${name.substring(0, 20)}...` : name}
      </h6>
    </div>
  );
}

export const wait = (seconds: number): Promise<void> => {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, seconds * 1000); // converting seconds to milliseconds
  });
};

export const getRoleColor = (role: string) => {
  switch (role) {
    case ROLES.ADMIN:
      return 'bg-blurple';
    case ROLES.EDITOR:
      return 'bg-trud-orange';
    case ROLES.VIEWER:
      return 'bg-risk-green';
    default:
      return 'bg-body-black';
  }
};

export const ShopperScoreOrder = (shopperscore: Config[]) => {
  return shopperscore?.length
    ? (ShopperConfigSequence.map((config_type) =>
        shopperscore.find((obj) => obj.config_type === config_type)
      ).filter((score) => score != undefined) as Config[])
    : [];
};

export const copyToClipboard = async (text: string, onCopy?: () => void) => {
  try {
    await navigator.clipboard.writeText(text);
    onCopy?.();
  } catch (error) {
    console.error('Failed to copy:', error);
  }
};

export const PTIProjectDetails = {
  api_key: 'sandbox_e4ef8457-7195-4f92-a29a-ada9df062254',
  id: 'e3ccda88-401b-4683-b6be-1f8f010c5fc9',
  name: 'APP Fraud Prevention',
  description:
    'Identify bank accounts associated with APP fraud and offering a name-to-IBAN match.',
  environment: 'SANDBOX',
  products: PAYEE_TRUST_INDEX,
  status: 'published',
  fraud_risk_type_id: '468bfc5d-b7b2-4e54-9051-e55bc2013ef6',
  project_id: 'dd3ada11-ed52-4626-9c5a-a1c0df9afe74',
  organization_id: '',
  published_at: null,
  api_key_detail: {
    api_key: 'sandbox_e4ef8457-7195-4f92-a29a-ada9df062254',
    environment: 'SANDBOX',
    project_id: 'dd3ada11-ed52-4626-9c5a-a1c0df9afe74',
  },
  projects: null,
  fraud_risk_type: {
    id: '468bfc5d-b7b2-4e54-9051-e55bc2013ef6',
    title: 'Payee trustworthiness',
    description:
      'Confirmation of Payee and an assessment of previous APP fraud and scam activity linked to the account.',
    type: 'PRESETS',
  },
  trusted_shopper_scores: [
    {
      id: 'a7d6ea94-6203-481c-a204-80c2021f5d0c',
      name: 'A Trusted Payee has an index score of ...',
      score: 80,
      config_type: 'LOW_RISK' as config_type,
      recommended: 80,
      description:
        'Trusted Payees can be confirmed in an account-to-name match and have no cases of reported APP fraud scams activity against their accounts.',
      environment: 'SANDBOX',
      project_id: 'bbc76fef-83d8-44c1-bc87-d26d879138be',
    },
    {
      id: '56903c72-921a-4e62-b458-0846b0873185',
      name: 'A Medium Risk Payee has an index score of...',
      score: '79',
      config_type: 'MEDIUM_RISK' as config_type,
      recommended: '79',
      description:
        'Medium Risk Payees can be confirmed in an account-to-name match  but have cases of reported APP fraud scams activity against their accounts.',
      environment: 'SANDBOX',
      project_id: 'bbc76fef-83d8-44c1-bc87-d26d879138be',
    },
    {
      id: '1aff524a-4d49-4598-a733-5515ec6d7121',
      name: 'A High Risk Payee has an index score of...',
      score: '69',
      config_type: 'LOW_RISK' as config_type,
      recommended: '69',
      description:
        'High Risk Payees cannot be confirmed in an account-to-name match and/or have cases of reported APP fraud scams activity against their accounts.',
      environment: 'SANDBOX',
      project_id: 'bbc76fef-83d8-44c1-bc87-d26d879138be',
    },
  ],
  low_risk_shopper_options: {
    'Pay after delivery': true,
    'Pay by installments': true,
    'Debit/Credit card': true,
    'Pay by Bank': true,
    'Apple Pay': true,
    'Google Pay': true,
  },
  medium_risk_shopper_options: {
    'Pay after delivery': false,
    'Pay by installments': true,
    'Debit/Credit card': true,
    'Pay by Bank': true,
    'Apple Pay': true,
    'Google Pay': true,
  },
  high_risk_shopper_options: {
    'Pay after delivery': false,
    'Pay by installments': false,
    'Debit/Credit card': true,
    'Pay by Bank': true,
    'Apple Pay': true,
    'Google Pay': true,
  },
  low_risk_refund_details: {
    refund_mode: 'Instant refund',
    refund_trigger: 'Return received by logistics partner',
    refund_execution: 'Immediately',
  },
  medium_risk_refund_details: {
    refund_mode: 'Refund after return',
    refund_trigger: 'Return received in warehouse',
    refund_execution: 'After',
    days_to_refund: 14,
  },
  high_risk_refund_details: {
    refund_mode: 'Refund after return',
    refund_trigger: 'Return approved for refund',
    refund_execution: 'After',
    days_to_refund: 14,
  },
};

export const PAYEE_TRUST_INDEX_ATTRIBUTES = [
  {
    id: 'e3b3710e-df5c-42aa-b0da-e4babcfae5d4',
    name: 'Banking',
    score: '50',
    config_type: 'BANKING',
    description: 'Confirmation of Payee, providing a name to account match',
    recommended: '50',
    sub_category: [
      {
        name: 'Name and account match',
        score: '50',
        description:
          'Verification of the recipient’s name and account as an exact match, no match or close match.',
        recommended: '50',
      },
    ],
  },
  {
    id: 'e3b3710e-df5c-42aa-b0da-e4babcfae5d5',
    name: 'Issuer data',
    score: '25',
    config_type: 'ISSUER_DATA',
    description:
      'Data shared by your organisation about accounts involved with APP fraud and scams.',
    recommended: '25',
    sub_category: [
      {
        name: 'APP fraud activity',
        score: '25',
        description:
          'Actual evidence of APP fraud and scam activity into the account based on the Payer’s Institution’s data',
        recommended: '25',
      },
    ],
  },
  {
    id: 'e3b3710e-df5c-42aa-b0da-e4babcfae5d6',
    name: 'Network intelligence',
    score: '25',
    config_type: 'NETWORK_INTELLIGENCE',
    description:
      'Data shared by other Issuers about accounts involved with APP fraud and scams.',
    recommended: '25',
    sub_category: [
      {
        name: 'APP fraud activity',
        score: '25',
        description:
          'Actual evidence of APP fraud and scam activity into the account based on data shared across the network',
        recommended: '25',
      },
    ],
  },
];

export const checkConsentAuthorisation = ({
  consent_status,
  request_timestamp,
}: {
  consent_status: string;
  request_timestamp: Date;
}) => {
  if (consent_status === 'ACTIVE' && moment(request_timestamp).isAfter(moment())) {
    return true;
  }
  return false;
};

export const generateLastMonthsList = (year: string) => {
  const lastMonthsList = [];
  const currentMonths = year === moment().year().toString() ? moment().month() : 11;

  for (let i = currentMonths; i >= 0; i--) {
    lastMonthsList.push({
      label: moment().month(i).format(`MMM`).toString().toUpperCase(),
      value: year + '-' + moment().month(i).format('MM'),
    });
  }
  return lastMonthsList;
};

// For Reason Code
export const ReasonCodeRiskLabel = (
  score: number,
  risk_level_criteria: {
    medium_risk_threshold: number;
    high_risk_threshold: number;
    max_score: number;
  }
) => {
  let lebelText = '';
  let color = '';
  if (score > risk_level_criteria.medium_risk_threshold) {
    lebelText = 'High trust';
    color = 'bg-score-green';
  } else if (score > risk_level_criteria.high_risk_threshold) {
    lebelText = 'Medium trust';
    color = 'bg-trud-orange';
  } else {
    lebelText = 'Low trust';
    color = 'bg-risk-red';
  }
  return (
    <>
      <div className={`w-2 h-2 ${color} rounded-full`}></div>
      <span className="text-base text-grey-content-secondary text-semibold">
        {lebelText}
      </span>
    </>
  );
};

export const truncateUrl = (url: string, maxLength: number = 35) => {
  if (url.length > maxLength) {
    return url.slice(0, maxLength) + '...';
  }
  return url;
};

export const convertStringToNumber = (value: string | number) => {
  return value ? (typeof value === 'number' ? value : parseInt(value.toString())) : 0;
};

export const generateRandomString = (length: number, symbols: string) => {
  const letters = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const allCharacters = letters + symbols;
  const charsetLength = allCharacters.length;

  const maxValid = Math.floor(256 / charsetLength) * charsetLength;

  let result = '';
  while (result.length < length) {
    const bytes = new Uint8Array(length - result.length);
    crypto.getRandomValues(bytes);

    for (const byte of bytes) {
      if (byte < maxValid) {
        result += allCharacters[byte % charsetLength];
      }
    }
  }

  return result;
};

export const generateSyndicateString = (isDash = true) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < 8; i++) {
    if (i === 4 && isDash) {
      result += '-';
    }
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const downloadFile = (filename: string, text: string) => {
  const blob = new Blob([text], { type: 'text/plain' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const fraudRiskTypeTrustDimension = {
  IDENTITY: 'Identity',
  CHARGEBACK_TRENDS: 'Chargeback Trends',
  RETURNS_TRENDS: 'Returns Trends',
  ITEM_NOT_RECEIVED_TRENDS: 'Item-Not-Received (INR) Trends',
  BUY_NOW_PAY_LATER: 'Buy-Now-Pay-Later (BNPL)',
  AUTHORISED_PUSH_PAYMENTS_FRAUD: 'Authorised Push Payment Fraud',
  MULING: 'Muling',
  BONUS_LOYALTY_AND_PROMOTIONS: 'Bonus, Loyalty and Promotions',
  PAYMENTS_AND_AFFORDABILITY: 'Payments and Affordability',
};

export const defaultFraudRiskTypeTrustDimentions = {
  [fraudRiskTypeTrustDimension['IDENTITY']]: 19,
  [fraudRiskTypeTrustDimension['CHARGEBACK_TRENDS']]: 18,
  [fraudRiskTypeTrustDimension['RETURNS_TRENDS']]: 15,
  [fraudRiskTypeTrustDimension['ITEM_NOT_RECEIVED_TRENDS']]: 8,
  [fraudRiskTypeTrustDimension['BUY_NOW_PAY_LATER']]: 9,
  [fraudRiskTypeTrustDimension['AUTHORISED_PUSH_PAYMENTS_FRAUD']]: 8,
  [fraudRiskTypeTrustDimension['MULING']]: 7,
  [fraudRiskTypeTrustDimension['BONUS_LOYALTY_AND_PROMOTIONS']]: 11,
  [fraudRiskTypeTrustDimension['PAYMENTS_AND_AFFORDABILITY']]: 5,
};

export const defaultTrustDimensionsRange = {
  [fraudRiskTypeTrustDimension['IDENTITY']]: {
    HighTrust: 19,
    MediumTrust: 15,
    LowTrust: 8,
  },
  [fraudRiskTypeTrustDimension['CHARGEBACK_TRENDS']]: {
    HighTrust: 18,
    MediumTrust: 14,
    LowTrust: 7,
  },
  [fraudRiskTypeTrustDimension['RETURNS_TRENDS']]: {
    HighTrust: 15,
    MediumTrust: 11,
    LowTrust: 6,
  },
  [fraudRiskTypeTrustDimension['ITEM_NOT_RECEIVED_TRENDS']]: {
    HighTrust: 8,
    MediumTrust: 6,
    LowTrust: 3,
  },
  [fraudRiskTypeTrustDimension['BUY_NOW_PAY_LATER']]: {
    HighTrust: 8,
    MediumTrust: 6,
    LowTrust: 3,
  },
  [fraudRiskTypeTrustDimension['AUTHORISED_PUSH_PAYMENTS_FRAUD']]: {
    HighTrust: 7,
    MediumTrust: 5,
    LowTrust: 3,
  },
  [fraudRiskTypeTrustDimension['MULING']]: {
    HighTrust: 7,
    MediumTrust: 5,
    LowTrust: 3,
  },
  [fraudRiskTypeTrustDimension['BONUS_LOYALTY_AND_PROMOTIONS']]: {
    HighTrust: 11,
    MediumTrust: 8,
    LowTrust: 4,
  },
  [fraudRiskTypeTrustDimension['PAYMENTS_AND_AFFORDABILITY']]: {
    HighTrust: 5,
    MediumTrust: 3,
    LowTrust: 2,
  },
};

export const reCalculateCTIScore = async (
  ctiScoreData: TrudentyData[],
  projectTrustDimension: Config[],
  segmentationScore: Config[]
) => {
  // Create a deep copy of the original data to avoid mutating the original state
  const newCTIScore = JSON.parse(JSON.stringify(ctiScoreData)) as TrudentyData[];

  if (newCTIScore.length) {
    newCTIScore.forEach((ctiScore) => {
      const ctiScoreValue = Math.round(ctiScore.consumer_trust_index);
      const lowTrustScore =
        segmentationScore.find((segmentation) => segmentation.config_type === 'HIGH_RISK')
          ?.score || 69;
      const highTrustScore =
        segmentationScore.find((segmentation) => segmentation.config_type === 'LOW_RISK')
          ?.score || 80;

      let riskLevel = ctiScore.risk_level;
      if (ctiScoreValue >= Math.round(Number(highTrustScore))) {
        riskLevel = 'High Trust';
      } else if (ctiScoreValue > Math.round(Number(lowTrustScore))) {
        riskLevel = 'Medium Trust';
      } else {
        riskLevel = 'Low Trust';
      }
      ctiScore.risk_level = riskLevel;
      ctiScore?.risk_features?.forEach((riskFeature) => {
        // Project Trust Dimension Score - riskFeatureScore (New Score from project)
        const riskFeatureScore = projectTrustDimension.find(
          (projectTrustDimension) =>
            projectTrustDimension.name === riskFeature.trust_dimension
        )?.score;
        // Default Trust Dimension Score (360) - defaultScore (Old score from Static)
        const defaultScore =
          defaultFraudRiskTypeTrustDimentions[
            riskFeature.trust_dimension as keyof typeof defaultFraudRiskTypeTrustDimentions
          ];
        // CTI Trust Dimension Score - oldTrustScore
        const oldTrustScore = riskFeature.trust_dimension_score;
        let trustScore = oldTrustScore; // Current CTI Trust Dimension Score - trustScore (From API)
        if (trustScore && riskFeatureScore) {
          trustScore = Math.floor(
            (trustScore * Math.round(Number(riskFeatureScore))) / defaultScore
          );
        }
        riskFeature.trust_dimension_score = trustScore;

        const defaultTrustScore =
          defaultTrustDimensionsRange[riskFeature.trust_dimension];
        const rangeSelector = calculateTrustRanges(
          defaultTrustScore.HighTrust,
          defaultTrustScore.MediumTrust,
          defaultTrustScore.LowTrust,
          trustScore,
          oldTrustScore
        );

        riskFeature.trust_dimension_score_range_HighRisk =
          rangeSelector.trust_dimension_score_range_HighRisk;
        riskFeature.trust_dimension_score_range_MediumRisk =
          rangeSelector.trust_dimension_score_range_MediumRisk;
        riskFeature.trust_dimension_score_range_Trusted =
          rangeSelector.trust_dimension_score_range_Trusted;

        riskFeature.feature_sets.forEach((featureSet) => {
          let featureSetScore = featureSet.feature_set_score; // CTI Feature Set Score - featureSetScore (From API)
          if (featureSetScore && riskFeatureScore) {
            featureSetScore = Math.floor(
              (featureSetScore * Math.round(Number(oldTrustScore))) / trustScore
            );
          }
          featureSet.feature_set_score = featureSetScore;
        });
      });
    });
  }
  return newCTIScore;
};

export const reCalculateNetworkDataScore = (
  networkData: NetworkData,
  segmentationScore: Config[]
) => {
  // Use shallow copy instead of expensive JSON.parse(JSON.stringify())
  // Only copy the structure, not deep clone the entire data
  if (!networkData?.nodes?.length) return networkData;

  // Pre-calculate scores once (outside the loop)
  const lowTrustScore =
    segmentationScore.find((segmentation) => segmentation.config_type === 'HIGH_RISK')
      ?.score || 69;
  const highTrustScore =
    segmentationScore.find((segmentation) => segmentation.config_type === 'LOW_RISK')
      ?.score || 80;

  const lowTrustThreshold = Math.round(Number(lowTrustScore));
  const highTrustThreshold = Math.round(Number(highTrustScore));

  // Create new array with updated nodes (shallow copy of nodes, update only risk_level)
  const newNetworkData: NetworkData = {
    ...networkData,
    nodes: networkData.nodes.map((node) => {
      const ctiScoreValue = Math.round(node.data.consumer_trust_index);

      let riskLevel: string;
      if (ctiScoreValue >= highTrustThreshold) {
        riskLevel = 'High Trust';
      } else if (ctiScoreValue > lowTrustThreshold) {
        riskLevel = 'Medium Trust';
      } else {
        riskLevel = 'Low Trust';
      }

      // Only create new node object if risk_level changed
      if (node.data.risk_level === riskLevel) {
        return node;
      }

      return {
        ...node,
        data: {
          ...node.data,
          risk_level: riskLevel,
        },
      };
    }),
    edges: networkData.edges, // Reuse edges array (no changes needed)
  };

  return newNetworkData;
};

export const calculateTrustRanges = (
  HighTrustScore: number,
  MediumTrustScore: number,
  LowTrustScore: number,
  newScore: number,
  oldScore: number
) => {
  const mediumTrustStart = ((LowTrustScore / oldScore) * newScore).toFixed(1);
  const mediumTrustEnd = ((MediumTrustScore / oldScore) * newScore).toFixed(1);
  const highTrustStart = ((MediumTrustScore / oldScore) * newScore).toFixed(1);
  const highTrustEnd = ((HighTrustScore / oldScore) * newScore).toFixed(1);
  return {
    trust_dimension_score_range_HighRisk: `${0}-${mediumTrustStart}`,
    trust_dimension_score_range_MediumRisk: `${mediumTrustStart}-${mediumTrustEnd}`,
    trust_dimension_score_range_Trusted: `${highTrustStart}-${highTrustEnd}`,
  };
};

export const lastDaysOptions = [
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 60 days', value: 60 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Past 6 months', value: 180 },
  { label: 'Past 12 months', value: 365 },
];

export const lastMonthsOptions = [
  { label: 'Past 12 months', value: 365 },
  { label: 'Past 24 months', value: 730 },
];

export const pastMonthsOptions = [
  { value: 90, label: 'Past 3 months' },
  { value: 180, label: 'Past 6 months' },
  { value: 365, label: 'Past 12 months' },
];

export const trustDimensionOptions = [
  {
    id: 'all',
    name: 'All',
  },
  {
    id: 'identity',
    name: fraudRiskTypeTrustDimension.IDENTITY,
  },
  {
    id: 'chargeback_trends',
    name: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
  },
  {
    id: 'refund_trends',
    name: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
  },
  {
    id: 'item_not_received_trends',
    name: fraudRiskTypeTrustDimension.ITEM_NOT_RECEIVED_TRENDS,
  },
  {
    id: 'buy_now_pay_later',
    name: fraudRiskTypeTrustDimension.BUY_NOW_PAY_LATER,
  },
  {
    id: 'authorised_push_payments_fraud',
    name: fraudRiskTypeTrustDimension.AUTHORISED_PUSH_PAYMENTS_FRAUD,
  },
  {
    id: 'muling',
    name: fraudRiskTypeTrustDimension.MULING,
  },
  {
    id: 'bonus_loyalty_and_promotions',
    name: fraudRiskTypeTrustDimension.BONUS_LOYALTY_AND_PROMOTIONS,
  },
  {
    id: 'payments_and_affordability',
    name: fraudRiskTypeTrustDimension.PAYMENTS_AND_AFFORDABILITY,
  },
];

export const SourceEnum = {
  TRUST_INDEX: 'Trust Index',
  REPORTED: 'Reported',
  PROXY: 'Proxy',
  FEEDBACK_LOOP: 'Feedback Loop',
};

export const sourceDropdownOptions = [
  {
    id: 'all',
    name: 'All',
  },
  {
    id: 'reported',
    name: SourceEnum.REPORTED,
  },
  {
    id: 'proxy',
    name: SourceEnum.PROXY,
  },
  {
    id: 'feedback_loop',
    name: SourceEnum.FEEDBACK_LOOP,
  },
];

export const impactTableData = {
  significant_signals_analysis: [
    {
      signal: 'Shared identity attributes',
      code: 'IDN2SID',
      trustDimension: fraudRiskTypeTrustDimension.IDENTITY,
      source: SourceEnum.TRUST_INDEX,
      contribution: 3.5,
      changePct: 2.3,
    },
    {
      signal: 'Account syndicate link',
      code: 'IDN3ASL',
      trustDimension: fraudRiskTypeTrustDimension.IDENTITY,
      source: SourceEnum.TRUST_INDEX,
      contribution: 3.2,
      changePct: 2.4,
    },
    {
      signal: 'Fraudulent chargeback requests',
      code: 'CHB4FRD',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.REPORTED,
      contribution: 2.9,
      changePct: 2.0,
    },
    {
      signal: 'Declined chargeback refund requests',
      code: 'CHB4DEC',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.FEEDBACK_LOOP,
      contribution: 2.4,
      changePct: 2.1,
    },
    {
      signal: 'Return requests resolution process',
      code: 'RET2RES',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      source: SourceEnum.REPORTED,
      contribution: 2.3,
      changePct: -2.2,
    },
    {
      signal: 'Trend of failed and reversed transactions',
      code: 'AFP1FRT',
      trustDimension: fraudRiskTypeTrustDimension.PAYMENTS_AND_AFFORDABILITY,
      source: SourceEnum.PROXY,
      contribution: 2.3,
      changePct: 2.4,
    },
    {
      signal: 'Historic settlement failures',
      code: 'AFP3HSF',
      trustDimension: fraudRiskTypeTrustDimension.PAYMENTS_AND_AFFORDABILITY,
      source: SourceEnum.PROXY,
      contribution: 2.2,
      changePct: 1.9,
    },
    {
      signal: 'Reversal, chargeback and refund clustering',
      code: 'AFP3RCR',
      trustDimension: fraudRiskTypeTrustDimension.PAYMENTS_AND_AFFORDABILITY,
      source: SourceEnum.PROXY,
      contribution: 2.2,
      changePct: 2.3,
    },
    {
      signal: 'Declined return and refund requests',
      code: 'RET4DEC',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      source: SourceEnum.PROXY,
      contribution: 2.1,
      changePct: 2.0,
    },
    {
      signal: 'Fraudulent return and refund requests',
      code: 'RET4FRD',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      source: SourceEnum.REPORTED,
      contribution: 2.1,
      changePct: 1.9,
    },
    {
      signal: 'Return\u2013Dispute conversion rate',
      code: 'RET1RTC',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      source: SourceEnum.PROXY,
      contribution: 2.0,
      changePct: 2.1,
    },
    {
      signal: 'Chargeback resolution effectiveness',
      code: 'CHB2EFF',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.FEEDBACK_LOOP,
      contribution: 2.0,
      changePct: 2.0,
    },
    {
      signal: 'INR resolution effectiveness',
      code: 'INR2EFF',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.FEEDBACK_LOOP,
      contribution: 1.9,
      changePct: 2.2,
    },
    {
      signal: 'Declined INR refund requests',
      code: 'INR4DEC',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.PROXY,
      contribution: 1.8,
      changePct: 2.0,
    },
    {
      signal: 'Fraudulent INR requests',
      code: 'INR4FRD',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.REPORTED,
      contribution: 1.7,
      changePct: 1.9,
    },
  ],
  ground_truth_proxy_analysis: [
    {
      source: SourceEnum.PROXY,
      signal: 'Shared identity attributes',
      code: 'IDN2SID',
      trustDimension: fraudRiskTypeTrustDimension.IDENTITY,
      contribution: 3.5,
      changePct: 2.3,
    },
    {
      source: SourceEnum.PROXY,
      signal: 'Coordinated fraud attacks',
      code: 'IDN3CFA',
      contribution: 1.6,
      changePct: 0,
    },
    {
      source: SourceEnum.REPORTED,
      signal: 'Prior fraud report on account/card',
      code: 'IDN4RFC',
      contribution: 1.6,
      changePct: 0,
    },
    {
      source: SourceEnum.PROXY,
      signal: 'Shared payment methods',
      code: 'IDN4SPM',
      contribution: 1.6,
      changePct: 0,
    },
    {
      source: SourceEnum.PROXY,
      signal: 'Trend of failed and reversed transactions',
      code: 'AFP1FRT',
      trustDimension: fraudRiskTypeTrustDimension.PAYMENTS_AND_AFFORDABILITY,
      contribution: 2.3,
      changePct: 2.4,
    },
    {
      source: SourceEnum.PROXY,
      signal: 'Historic settlement failures',
      code: 'AFP3HSF',
      trustDimension: fraudRiskTypeTrustDimension.PAYMENTS_AND_AFFORDABILITY,
      contribution: 2.2,
      changePct: 1.9,
    },
    {
      source: SourceEnum.PROXY,
      signal: 'Reversal, chargeback and refund clustering',
      code: 'AFP3RCR',
      trustDimension: fraudRiskTypeTrustDimension.PAYMENTS_AND_AFFORDABILITY,
      contribution: 2.2,
      changePct: 2.3,
    },
    {
      source: SourceEnum.PROXY,
      signal: 'Return and refund value to order value',
      code: 'RET1VAL',
      contribution: 1.6,
      changePct: 0,
    },
    {
      source: SourceEnum.PROXY,
      signal: 'Return-Dispute conversion rate',
      code: 'RET1RTC',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      contribution: 2.0,
      changePct: 2.1,
    },
    {
      source: SourceEnum.PROXY,
      signal: 'Declined return and refund requests',
      code: 'RET4DEC',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      contribution: 2.1,
      changePct: 2.0,
    },
    {
      source: SourceEnum.REPORTED,
      signal: 'Fraudulent return and refund requests',
      code: 'RET4FRD',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      contribution: 2.1,
      changePct: 1.9,
    },
    {
      source: SourceEnum.FEEDBACK_LOOP,
      signal: 'Chargeback rates',
      code: 'CHB1RAT',
      contribution: 1.6,
      changePct: 0,
    },
    {
      source: SourceEnum.FEEDBACK_LOOP,
      signal: 'Chargeback resolution effectiveness',
      code: 'CHB2EFF',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      contribution: 2.0,
      changePct: 2.0,
    },
    {
      source: SourceEnum.FEEDBACK_LOOP,
      signal: 'Declined chargeback refund requests',
      code: 'CHB4DEC',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      contribution: 2.4,
      changePct: 2.1,
    },
    {
      source: SourceEnum.REPORTED,
      signal: 'Fraudulent chargeback requests',
      code: 'CHB4FRD',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      contribution: 2.9,
      changePct: 2.0,
    },
    {
      source: SourceEnum.FEEDBACK_LOOP,
      signal: 'INR resolution effectiveness',
      code: 'INR2EFF',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      contribution: 1.9,
      changePct: 2.2,
    },
    {
      source: SourceEnum.PROXY,
      signal: 'Declined INR refund requests',
      code: 'INR4DEC',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      contribution: 1.8,
      changePct: 2.0,
    },
    {
      source: SourceEnum.REPORTED,
      signal: 'Fraudulent INR requests',
      code: 'INR4FRD',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      contribution: 1.7,
      changePct: 1.9,
    },
    {
      source: SourceEnum.REPORTED,
      signal: 'ACH fraud',
      code: '-',
      contribution: 1.6,
      changePct: 0,
    },
    {
      source: SourceEnum.PROXY,
      signal: 'Provisional credits',
      code: '-',
      contribution: 1.6,
      changePct: 0,
    },
  ],
  trust_index_impact_analysis: [
    {
      signal: 'Shared identity attributes',
      code: 'IDN2SID',
      trustDimension: fraudRiskTypeTrustDimension.IDENTITY,
      source: SourceEnum.REPORTED,
      contribution: 3.5,
      changePct: 2.3,
    },
    {
      signal: 'Account syndicate link',
      code: 'IDN3ASL',
      trustDimension: fraudRiskTypeTrustDimension.IDENTITY,
      source: SourceEnum.TRUST_INDEX,
      contribution: 3.2,
      changePct: 2.4,
    },
    {
      signal: 'Fraudulent chargeback requests',
      code: 'CHB4FRD',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.REPORTED,
      contribution: 2.9,
      changePct: 2.0,
    },
    {
      signal: 'Declined chargeback refund requests',
      code: 'CHB4DEC',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.FEEDBACK_LOOP,
      contribution: 2.4,
      changePct: 2.1,
    },
    {
      signal: 'Return requests resolution process',
      code: 'RET2RES',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      source: SourceEnum.TRUST_INDEX,
      contribution: 2.3,
      changePct: 2.2,
    },
    {
      signal: 'Trend of failed and reversed transactions',
      code: 'AFP1FRT',
      trustDimension: fraudRiskTypeTrustDimension.PAYMENTS_AND_AFFORDABILITY,
      source: SourceEnum.PROXY,
      contribution: 2.3,
      changePct: 2.4,
    },
    {
      signal: 'Historic settlement failures',
      code: 'AFP3HSF',
      trustDimension: fraudRiskTypeTrustDimension.PAYMENTS_AND_AFFORDABILITY,
      source: SourceEnum.PROXY,
      contribution: 2.2,
      changePct: 1.9,
    },
    {
      signal: 'Reversal, chargeback and refund clustering',
      code: 'AFP3RCR',
      trustDimension: fraudRiskTypeTrustDimension.PAYMENTS_AND_AFFORDABILITY,
      source: SourceEnum.PROXY,
      contribution: 2.2,
      changePct: 2.3,
    },
    {
      signal: 'Declined return and refund requests',
      code: 'RET4DEC',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      source: SourceEnum.PROXY,
      contribution: 2.1,
      changePct: 2.0,
    },
    {
      signal: 'Fraudulent return and refund requests',
      code: 'RET4FRD',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      source: SourceEnum.REPORTED,
      contribution: 2.1,
      changePct: 1.9,
    },
    {
      signal: 'Return\u2013Dispute conversion rate',
      code: 'RET1RTC',
      trustDimension: fraudRiskTypeTrustDimension.RETURNS_TRENDS,
      source: SourceEnum.PROXY,
      contribution: 2.0,
      changePct: 2.1,
    },
    {
      signal: 'Chargeback resolution effectiveness',
      code: 'CHB2EFF',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.FEEDBACK_LOOP,
      contribution: 2.0,
      changePct: 2.0,
    },
    {
      signal: 'INR resolution effectiveness',
      code: 'INR2EFF',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.FEEDBACK_LOOP,
      contribution: 1.9,
      changePct: 2.2,
    },
    {
      signal: 'Declined INR refund requests',
      code: 'INR4DEC',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.PROXY,
      contribution: 1.8,
      changePct: 2.0,
    },
    {
      signal: 'Fraudulent INR requests',
      code: 'INR4FRD',
      trustDimension: fraudRiskTypeTrustDimension.CHARGEBACK_TRENDS,
      source: SourceEnum.REPORTED,
      contribution: 1.7,
      changePct: 1.9,
    },
  ],
};
